import React, { Fragment, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import icon from '../../images/icon/magic.svg';
import Seo from '../../components/Seo';

const IndustriesPage = () => {
    const { t, i18n } = useTranslation();
    const { lang } = useParams();

    useEffect(() => {
        if (lang && (lang === 'en' || lang === 'ka')) {
            i18n.changeLanguage(lang);
        }
    }, [lang, i18n]);

    const industries = [
        {
            key: 'personal',
            icon: '👤',
            desc: 'Custom software solutions tailored for individuals and personal productivity.'
        },
        {
            key: 'business',
            icon: '💼',
            desc: 'Enterprise-grade software solutions designed to streamline operations and drive growth.'
        },
        {
            key: 'gov',
            icon: '🏛️',
            desc: 'Secure, compliant, and transparent digital solutions for government entities.'
        },
        {
            key: 'learning',
            icon: '📚',
            desc: 'Comprehensive e-learning and educational technology solutions.'
        },
        {
            key: 'ai',
            icon: '🤖',
            desc: 'Cutting-edge artificial intelligence and machine learning solutions.'
        }
    ];

    return (
        <Fragment>
            <Seo
                title="Industries | OrbitroniX"
                description="Explore our industry-specific software solutions for Personal, Business, Government, Learning, and AI sectors."
                keywords="software solutions, industry solutions, OrbitroniX"
                url={`/${lang}/industries`}
            />
            <Layout>
                <motion.section
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="page-title pt-150 pos-rel bg_img"
                >
                    <div className="container">
                        <div className="page-title-wrap sd-title-wrap">
                            <div className="row mt-none-30 align-items-end">
                                <div className="col-lg-9 mt-30">
                                    <motion.div
                                        initial={{ opacity: 0, x: -50 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.3 }}
                                        className="page-title-box"
                                    >
                                        <motion.span
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.5 }}
                                            className="sub-title"
                                        >
                                            <img src={icon} alt="" /> {t('footer.industries') || 'Industries'}
                                        </motion.span>
                                        <motion.h2
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.7 }}
                                            className="title"
                                        >
                                            {t('footer.industries') || 'Industries'}
                                        </motion.h2>
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>

                <motion.section
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                    className="terms&conditions-section pb-85 cp-det-bg"
                >
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-10">
                                <div className="row g-4">
                                    {industries.map((industry, index) => (
                                        <motion.div
                                            key={industry.key}
                                            className="col-md-6 col-lg-4"
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.7 + index * 0.1 }}
                                        >
                                            <Link
                                                to={`/${lang}/industries/${industry.key}`}
                                                className="industry-card d-block text-decoration-none"
                                                aria-label={`${t('common.learnMore')} - ${t(`industries.${industry.key}`)}`}
                                            >
                                                <div className="industry-card-inner item-details-widget p-4 h-100">
                                                    <div className="industry-icon mb-3">{industry.icon}</div>
                                                    <h4 className="text-white mb-3">{t(`industries.${industry.key}`)}</h4>
                                                    <p className="text-white-50 mb-4" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                                        {industry.desc}
                                                    </p>
                                                    <span className="orbx-btn mt-auto">
                                                        {t('common.learnMore')} <i className="fas fa-arrow-right"></i>
                                                    </span>
                                                </div>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>

                <style>{`
                    .industry-card {
                        transition: transform 0.3s ease;
                    }
                    .industry-card:hover {
                        transform: translateY(-5px);
                    }
                    .industry-card:hover .industry-card-inner {
                        border-color: rgba(108, 92, 231, 0.4);
                    }
                    .industry-card-inner {
                        transition: border-color 0.3s ease;
                        display: flex;
                        flex-direction: column;
                    }
                    .industry-icon {
                        font-size: 48px;
                        line-height: 1;
                    }
                `}</style>
            </Layout>
        </Fragment>
    );
};

export default IndustriesPage;
